import { forwardRef, useEffect, useState, type ComponentPropsWithoutRef, type CSSProperties } from 'react';
import { Box } from '@mantine/core';
import { useDanceStyleLoaderStyles } from './DanceStyleLoaderContext';
import { usePrefersReducedMotion } from '../../../src/hooks/usePrefersReducedMotion';
import {
  LOADER_TEMPO,
  REDUCED_MOTION_SLOWDOWN,
  TINY_LOADER_TYPES,
  tinyRhythmForStyles,
  type TinyLoaderType,
  type TinyRhythm,
} from '../../../src/constants/danceLoaders';
import './DanceLoaders.css';

// Steps through a rhythm, returning the lit beat and whether it's accented.
// Slower with reduced motion; the CSS drops the scale and lift (#1940).
function useRhythm({ steps }: TinyRhythm) {
  const [step, setStep] = useState(0);
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    const ms = (steps[step][1] / LOADER_TEMPO) * (reduced ? REDUCED_MOTION_SLOWDOWN : 1);
    const timer = setTimeout(() => setStep((s) => (s + 1) % steps.length), ms);
    return () => clearTimeout(timer);
  }, [step, steps, reduced]);
  const [active, , accent] = steps[step];
  return { active, accent: !!accent };
}

function BeatDots({ rhythm }: { rhythm: TinyRhythm }) {
  const { active, accent } = useRhythm(rhythm);
  return (
    <>
      {Array.from({ length: rhythm.beats }, (_, i) => (
        <span
          key={i}
          className={`tiny-dance-dot${active === i ? ' tiny-dance--on' : ''}${active === i && accent ? ' tiny-dance--accent' : ''}`}
        />
      ))}
    </>
  );
}

// Dance-shoe print: sole under the ball of the foot plus a separate heel block.
// Drawn as a right foot; the left is mirrored.
const SHOE_PATH =
  'M4.6 0.4 C6.4 0.4 8.6 2.4 9.1 5.2 C9.6 8.2 8.4 11.3 6.9 12.4 C5.9 13.1 4.4 13 3.6 12.1 ' +
  'C2.4 10.8 2.1 8.6 2.3 6 C2.5 3 3.2 0.4 4.6 0.4 Z ' +
  'M4 15 H7.1 C7.4 15 7.6 15.2 7.6 15.5 L7.4 18.9 C7.4 19.3 7.1 19.6 6.7 19.6 H4.4 ' +
  'C4 19.6 3.7 19.3 3.7 18.9 L3.5 15.5 C3.5 15.2 3.7 15 4 15 Z';

function Footsteps({ rhythm }: { rhythm: TinyRhythm }) {
  const { active } = useRhythm(rhythm);
  // Feet alternate on each lit beat: left on even beats, right on odd.
  const litFoot = active === null ? null : active % 2;
  return (
    <>
      {(['left', 'right'] as const).map((side, i) => (
        <svg
          key={side}
          viewBox="0 0 10 20"
          className={`tiny-dance-shoe tiny-dance-shoe--${side}${litFoot === i ? ' tiny-dance--on' : ''}`}
        >
          <path d={SHOE_PATH} transform={side === 'left' ? 'translate(10 0) scale(-1 1)' : undefined} />
        </svg>
      ))}
    </>
  );
}

type TinyDanceLoaderProps = ComponentPropsWithoutRef<'span'> & { kind?: TinyLoaderType };

// Plugs into Mantine's <Loader> through the theme (see theme/index.ts), so every
// loading button, ActionIcon and ThemedSpinner uses it. Mantine passes size and
// color as --loader-size / --loader-color on the root class.
export const TinyDanceLoader = forwardRef<HTMLSpanElement, TinyDanceLoaderProps>(
  ({ kind, className, style, ...others }, ref) => {
    const pageStyles = useDanceStyleLoaderStyles();
    const [picked] = useState<TinyLoaderType>(
      () => kind ?? TINY_LOADER_TYPES[Math.floor(Math.random() * TINY_LOADER_TYPES.length)]
    );
    const rhythm = tinyRhythmForStyles(pageStyles);

    return (
      <Box
        component="span"
        ref={ref}
        role="status"
        aria-label="Loading…"
        className={`tiny-dance tiny-dance--${picked}${className ? ` ${className}` : ''}`}
        {...others}
        style={{ '--dance-tempo': LOADER_TEMPO, ...style } as CSSProperties}
      >
        {picked === 'equalizer' && (
          <>
            <span className="tiny-dance-bar" />
            <span className="tiny-dance-bar" />
            <span className="tiny-dance-bar" />
          </>
        )}
        {picked === 'dots' && <BeatDots rhythm={rhythm} />}
        {picked === 'footsteps' && <Footsteps rhythm={rhythm} />}
      </Box>
    );
  }
);

TinyDanceLoader.displayName = 'TinyDanceLoader';
